import React, { memo, useMemo } from 'react'
import tw from 'twin.macro'
import { useSelector } from 'react-redux'
import { GSIcon } from 'src/components/shared/GSIcon'
import { RowActionsMenu } from 'src/components/shared/grid/RowActionsMenu'
import type { RemoteConfig } from 'src/interfaces/remoteConfig'
import type { MenuActionItem } from 'src/interfaces/ui'
import { getRemoteConfigDeletingId } from 'src/selectors/remoteConfig'

interface Props {
  remoteConfig: RemoteConfig
  onEdit(remoteConfig: RemoteConfig): void
  onDelete(remoteConfig: RemoteConfig): void
}

const RemoteConfigActionsCellComponent = ({ remoteConfig, onEdit, onDelete }: Props) => {
  const deletingId = useSelector(getRemoteConfigDeletingId)
  const isDeleting = deletingId === remoteConfig.id

  const items = useMemo<MenuActionItem[]>(() => {
    return [
      {
        label: 'Edit',
        icon: <GSIcon iconName="gs_pencil" size={20} css={tw`text-shades-1000`} />,
        disabled: isDeleting,
        dataTestId: `remote-config-edit-${remoteConfig.id}`,
        onClick: () => {
          onEdit(remoteConfig)
        },
      },
      {
        label: isDeleting ? 'Deleting…' : 'Delete',
        icon: <GSIcon iconName="gs_trash_can" size={20} css={tw`text-critical-500`} />,
        color: 'var(--mui-critical-500)',
        disabled: isDeleting,
        dataTestId: `remote-config-delete-${remoteConfig.id}`,
        onClick: () => {
          onDelete(remoteConfig)
        },
      },
    ]
  }, [remoteConfig, isDeleting, onEdit, onDelete])

  return (
    <RowActionsMenu
      items={items}
      ariaLabel={`Actions for ${remoteConfig.key}`}
      dataTestId={`remote-config-actions-${remoteConfig.id}`}
    />
  )
}

export const RemoteConfigActionsCell = memo(RemoteConfigActionsCellComponent)
